import { useMemo } from 'react';

import useAccountQuery from 'api/queries/useAccountQuery';
import useRescuedAisleQuery from 'api/queries/useRescuedAisleQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import { RESCUED_AISLE } from 'constants/shop';
import AisleContent from 'features/shop/AisleContent';
import { dietaryFilterData } from 'features/shop/filters/DietaryFilters/DietaryFiltersData';
import useGlobalStore from 'hooks/useGlobalStore';
import { getRescuedAisleItemCount } from 'utils/aislesUtils';
import { filterProducts, getAvailableFilters } from 'utils/filterUtils';

import { getRescuedAisleBanners } from './rescuedBannerUtils';

function Rescued() {
    const { appliedFilters } = useGlobalStore();
    const { data: account = {} } = useAccountQuery();
    const { data: subscription = {} } = useSubscriptionQuery();
    const { data: rescuedAislePayload, isLoading: aisleDataIsLoading } = useRescuedAisleQuery();

    // L2 nav / AisleContent require a numeric id for section anchors; API may only provide rankNumber.
    const aisles = useMemo(
        () =>
            (rescuedAislePayload?.aisles ?? []).map((aisle, index) => ({
                ...aisle,
                id: aisle.id ?? aisle.rankNumber ?? index + 1,
            })),
        [rescuedAislePayload]
    );
    const itemCount = getRescuedAisleItemCount(rescuedAislePayload);
    const banners = useMemo(() => getRescuedAisleBanners(aisles), [aisles]);

    const dietaryFilteredItems = useMemo(() => filterProducts(appliedFilters, aisles, []), [appliedFilters, aisles]);
    const hasEBTCard = !!subscription?.ebtCard?.last4;
    const availableFilters = useMemo(
        () =>
            // Gets filters that are **applicable** to the items in the aisle.
            getAvailableFilters(
                dietaryFilterData,
                aisles,
                [],
                account?.featureFlags?.featureSnapEnabled,
                hasEBTCard
                // Removes filters that aren't relevant to the aisle.
            ).filter((filter) => !['Rescued', 'Last Chance', 'Impact'].includes(filter.name)),
        [aisles, account?.featureFlags?.featureSnapEnabled, hasEBTCard]
    );

    return (
        <AisleContent
            banners={banners}
            categories={dietaryFilteredItems}
            currentAisle={{
                id: RESCUED_AISLE.id,
                name: RESCUED_AISLE.name,
                inStockCount: itemCount,
            }}
            currentCategory={{ id: 'all', name: 'All' }}
            isLoading={aisleDataIsLoading}
            filtersData={availableFilters}
        />
    );
}

export { Rescued as Component };
